import { useAuth } from '../contexts/AuthContext';
import { useCanWriteCompanySettings } from './useCanWriteCompanySettings';
import { canMarkPayPeriodPaid, canReopenPayPeriod } from '../lib/roles';

/**
 * Which /payroll controls this user may be shown.
 *
 * ONE FLAG PER API GATE, NOT ONE RULE FOR THE SCREEN (the fe#691 pattern, as
 * useExpensePermissions does it). This screen fires ELEVEN endpoints across
 * TWO routers and they are gated FOUR different ways. Reading one off another
 * is how a control appears that the API refuses — and on this screen the
 * refused control is `Approve All`, which every role could see and only an
 * admin could use.
 *
 * Read from fieldiq-api, verified against the route files rather than assumed:
 *
 * ── UNGATED ─────────────────────────────────────────────────────────────────
 *   GET /payroll/pay-periods                    payroll.routes.ts:71
 *       No role gate at all beyond auth. EVERY authenticated role lists pay
 *       periods, so the register itself is never refused and nothing here
 *       hides rows.
 *
 * ── requireRole('admin', 'office_staff') — NON-strict, elevated dispatcher
 *    passes ────────────────────────────────────────────────────────────────
 *   GET  /payroll/pay-periods/:id               :483   → canReadDetail
 *   GET  /payroll/pay-periods/:id/adjustments   :1773  → canReadDetail
 *   POST /payroll/pay-periods                   :101   ┐
 *   POST /payroll/pay-periods/auto-create       :150   │
 *   POST /payroll/pay-periods/:id/generate      :535   ├→ canEditPeriod
 *   POST /payroll/pay-periods/:id/save          :558   │
 *   POST /payroll/pay-periods/:id/export        :713   │
 *   POST /payroll/pay-periods/:id/add-employee  :320   │
 *   POST /payroll/pay-periods/:id/adjustments   :1689  ┘
 *
 * ── requireRole('admin', { strict: true }) — an elevated dispatcher does NOT
 *    pass ──────────────────────────────────────────────────────────────────
 *   POST   /payroll/pay-periods/:id/approve     :677   → canApprove
 *   POST   /pay-periods/:id/mark-paid           :425   → canMarkPaid
 *   POST   /pay-periods/:id/reopen              :370   → canReopen
 *   DELETE /pay-periods/:id                     :464   → canDeletePeriod
 *   PUT    /pay-periods/config                  :249   → canConfigurePayPeriods
 *
 * ── WHY canReadDetail AND canEditPeriod ARE SEPARATE FIELDS ─────────────────
 * They carry the same rule today and they are two different questions. Reading
 * decides whether a row opens at all; editing decides which controls the
 * opened period offers. A route moving must not silently move the other, which
 * is the reasoning roles.ts already gives for keeping `canMarkPayPeriodPaid`
 * and `canReopenPayPeriod` apart while their bodies are identical.
 *
 * ── THE ROLES THAT MAKE THIS WORTH A HOOK ───────────────────────────────────
 * `supervisor` and `logistics` reach this screen — the list has no gate — and
 * are refused the DETAIL, so every drill-down 403s for them. A plain
 * `dispatcher` (one whose company has `dispatcher_admin_access` off) is in the
 * same position. An ELEVATED dispatcher may generate, save, export and add
 * employees but may NOT approve, mark paid, reopen or delete. `office_staff`
 * may do everything non-strict and none of the strict five. No single rule for
 * the screen gets more than one of those right.
 *
 * Not `isAdminRole`: it admits the legacy platform role `owner`, which appears
 * in none of these route lists, so an owner would be shown controls that 403.
 */

/** Roles the non-strict `requireRole('admin', 'office_staff')` doors admit. */
export interface PayrollPermissions {
  /** GET a period's detail and its adjustments. */
  canReadDetail: boolean;
  /** Create, auto-create, generate, save, export, add employee, add adjustment. */
  canEditPeriod: boolean;
  /** POST .../approve — STRICT admin. */
  canApprove: boolean;
  /** POST /pay-periods/:id/mark-paid — STRICT admin, and one-way. */
  canMarkPaid: boolean;
  /** POST /pay-periods/:id/reopen — STRICT admin. */
  canReopen: boolean;
  /** DELETE /pay-periods/:id — STRICT admin. */
  canDeletePeriod: boolean;
  /** PUT /pay-periods/config — STRICT admin (api#736 put its siblings here too). */
  canConfigurePayPeriods: boolean;
}

export function usePayrollPermissions(): PayrollPermissions {
  const { user } = useAuth();
  const role = user?.role;

  // Resolves the elevated dispatcher for the NON-strict doors only. While the
  // per-tenant flag is loading a dispatcher reads as not allowed, so controls
  // appear rather than being shown and then taken away.
  const adminOrElevated = useCanWriteCompanySettings();
  const adminOrOffice = adminOrElevated || role === 'office_staff';

  // The strict five ask the same question five times on purpose; each mirrors
  // one route, and `strict: true` is what opts them out of the elevation above.
  const strictAdmin = role === 'admin';

  return {
    canReadDetail: adminOrOffice,
    canEditPeriod: adminOrOffice,
    canApprove: strictAdmin,
    canMarkPaid: canMarkPayPeriodPaid(role),
    canReopen: canReopenPayPeriod(role),
    canDeletePeriod: strictAdmin,
    canConfigurePayPeriods: strictAdmin,
  };
}
